import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';

import useAppDispatch from 'hooks/common/useAppDispatch';
import useAppSelector from 'hooks/common/useAppSelector';
import { ApplicationEducationLevel } from 'models/applications/enum';
import { EducationalLevel } from 'models/staff/upload-queue';
import { createToast } from 'redux/actions';
import { selectCurrentStaffContract } from 'redux/selectors/staff/contracts';

import { contractApi } from '../../redux/api/applications/contract-api';
import { staffContractsSlice } from '../../redux/slices/staff/staffContractsSlice';
import { useDownloadContractCustomerConsent } from '../statements/contract/useDownloadContractCustomerConsent';
import {
  useDownloadContractDocument,
  useDownloadTemplateDocument,
} from '../statements/contract/useDownloadContractDocument';
import { useDownloadInvoiceDocument } from '../statements/contract/useDownloadInvoiceDocument';
import { useDownloadMaternalCapitalSupplementalDocument } from '../statements/contract/useDownloadMaternalCapitalSupplementalDocument';
import { useDownloadPaymentRequest } from '../statements/contract/useDownloadPaymentRequest';

const useModeratorContractIdInfoForm = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const [sendContractForEditing] =
    contractApi.useLazySendContractForEditingQuery();
  const [approveContract] = contractApi.useLazyApproveContractQuery();
  const [signContract] = contractApi.useSignContractMutation();
  const [deleteContract] = contractApi.useLazyDeleteContractQuery();
  const [takeToWorkContract] = contractApi.useLazyTakeToWorkQuery();
  const [confirmContract] = contractApi.useLazyConfirmContractQuery();
  const [cancelContract] = contractApi.useLazyCancelContractQuery();
  const [receiveContract] = contractApi.useLazyReceiveContractQuery();

  const downloadPrintContract = useDownloadContractDocument();
  const downloadTemplate = useDownloadTemplateDocument();
  const downloadPrintCustomerConsent = useDownloadContractCustomerConsent();
  const downloadPrintMaternalCapitalSupplemental =
    useDownloadMaternalCapitalSupplementalDocument();
  const downloadPrintInvoice = useDownloadInvoiceDocument();
  const downloadPrintPaymentRequest = useDownloadPaymentRequest();

  const currentStaffContract = useAppSelector(selectCurrentStaffContract);
  const contract = currentStaffContract?.contract;
  const user = currentStaffContract?.user;

  const { register, control } = useForm<{ moderator: number }>({
    mode: 'all',
  });

  const fields = {
    moderator: register('moderator'),
  };

  const reject = id =>
    deleteContract(id)
      .unwrap()
      .then(() => {
        dispatch(createToast('Договор удалён', 'success'));
        navigate('../contracts');
      });

  const sendForEditing = id =>
    sendContractForEditing(id)
      .unwrap()
      .then(result => {
        dispatch(staffContractsSlice.actions.updateContract(result));
      });

  const sign = async (id, key) =>
    signContract({ id, key })
      .unwrap()
      .then(result => {
        dispatch(createToast('Договор подписан', 'success'));
        dispatch(staffContractsSlice.actions.updateContract(result));
      })
      .catch(error => {
        dispatch(createToast(error.data.messageRu, 'danger'));
      });

  const approve = id =>
    approveContract(id)
      .unwrap()
      .then(result => {
        dispatch(createToast('Договор одобрен', 'success'));
        dispatch(staffContractsSlice.actions.updateContract(result));
      })
      .catch(error => {
        dispatch(createToast(error.data.messageRu, 'danger'));
      });

  const takeToWork = id =>
    takeToWorkContract(id)
      .unwrap()
      .then(result => {
        dispatch(createToast('Договор взят в работу', 'success'));
        dispatch(staffContractsSlice.actions.updateContract(result));
      });

  const receive = id =>
    receiveContract(id)
      .unwrap()
      .then(result => {
        dispatch(createToast('Договор получен', 'success'));
        dispatch(staffContractsSlice.actions.updateContract(result));
      });

  const confirm = id =>
    confirmContract(id)
      .unwrap()
      .then(result => {
        dispatch(createToast('Договор утверждён', 'success'));
        dispatch(staffContractsSlice.actions.updateContract(result));
      });

  const cancel = id =>
    cancelContract(id)
      .unwrap()
      .then(result => {
        dispatch(createToast('Договор аннулирован', 'success'));
        dispatch(staffContractsSlice.actions.updateContract(result));
        return result;
      });

  const sspvoLink = () => {
    const isSecondary =
      contract?.applicationEducationLevel ===
      ApplicationEducationLevel.SECONDARY;
    const epguId = user?.epguIdentifiers?.find(item =>
      isSecondary
        ? [
            EducationalLevel.PRIMARY_VOCATIONAL,
            EducationalLevel.SECONDARY_VOCATIONAL,
          ].includes(item.educationalLevel)
        : [
            EducationalLevel.HIGHER_EDUCATION,
            EducationalLevel.POSTGRADUATE,
          ].includes(item.educationalLevel)
    )?.epguId;
    if (!epguId) return '';
    return isSecondary
      ? `https://priem-online.ru/spo/admission/entrants/${epguId}/personal`
      : `https://priem-online.ru/vo/admission/entrants/${epguId}/profile`;
  };

  return {
    control,
    fields,
    reject,
    sendForEditing,
    approve,
    sign,
    downloadPrintContract,
    downloadTemplate,
    takeToWork,
    receive,
    confirm,
    cancel,
    downloadPrintCustomerConsent,
    downloadPrintMaternalCapitalSupplemental,
    downloadPrintInvoice,
    downloadPrintPaymentRequest,
    sspvoLink,
  };
};

export default useModeratorContractIdInfoForm;
